// ============================================================================
// EVIDENCE MAP — Pack-Local Evidence Assist authoring model (Phase 2C-1)
//
// Evidence maps are NEVER-PHI. They contain only Pack concepts, aliases,
// keywords, phrases, units, and search rules authored by the specialist. They
// MUST NEVER contain patient values, OCR snippets, names, DOB, MRN, contact
// info, insurance IDs, filenames, storage paths, or any clinical document text
// from a real patient.
//
// This module is PURE (no Firebase, no I/O) so it can be reused by:
//   - the Pack Builder (authoring + publish validation)          [Phase 2C-1]
//   - normalizePackDoc (consistent normalization on read)         [Phase 2C-1]
//   - the matcher Cloud Function (flatten + search, later)        [Phase 2C-3]
//
// Phase 2C-1 SCOPE: authoring shape, normalization, validation, and the
// document-type vocabulary reconciliation. There is NO matching, NO candidate
// writes, and NO Gate chips here. Evidence found is never a clinical answer.
// ============================================================================

// ── Canonical evidence document-type enum ───────────────────────────────────
// SINGLE canonical vocabulary for evidenceMap.documentTypeHints. Intentionally
// identical to PACKET_DOCUMENT_CLASSIFICATIONS in store.packets.js (the packet
// document classification enum). Kept here as a pure constant so this module
// stays Firebase-free; the two lists MUST stay in sync.
export const EVIDENCE_DOCUMENT_TYPES = Object.freeze([
  'REFERRAL_LETTER',
  'CLINICAL_NOTE',
  'LAB_RESULT',
  'IMAGING_REPORT',
  'INSURANCE_CARD',
  'MEDICATION_LIST',
  'DISCHARGE_SUMMARY',
  'CONSULT_NOTE',
  'OTHER',
  'UNKNOWN',
]);

// User-facing labels for the canonical evidence document-type enum.
export const EVIDENCE_DOCUMENT_TYPE_LABELS = Object.freeze({
  REFERRAL_LETTER:   'Referral letter',
  CLINICAL_NOTE:     'Clinical note',
  LAB_RESULT:        'Lab result',
  IMAGING_REPORT:    'Imaging report',
  INSURANCE_CARD:    'Insurance card',
  MEDICATION_LIST:   'Medication list',
  DISCHARGE_SUMMARY: 'Discharge summary',
  CONSULT_NOTE:      'Consult note',
  OTHER:             'Other',
  UNKNOWN:           'Unknown',
});

// ── Reconciliation map: legacy / attachment-type vocab → canonical ──────────
// Used ONLY for evidence authoring + validation. Does NOT change attachment
// behavior anywhere else in the app. It lets loaded/imported packs and the
// attachmentType enum (CLINICAL_PHOTO / LABS / IMAGING_REPORT / REFERRAL_NOTE /
// INSURANCE / MED_LIST / OTHER_DOC) resolve to a canonical evidence hint.
const DOCUMENT_TYPE_RECONCILIATION = Object.freeze({
  LABS:           'LAB_RESULT',
  LAB:            'LAB_RESULT',
  IMAGING:        'IMAGING_REPORT',
  REFERRAL_NOTE:  'REFERRAL_LETTER',
  REFERRAL:       'REFERRAL_LETTER',
  INSURANCE:      'INSURANCE_CARD',
  MED_LIST:       'MEDICATION_LIST',
  MEDICATION:     'MEDICATION_LIST',
  OTHER_DOC:      'OTHER',
  CLINICAL_PHOTO: 'OTHER', // no canonical "photo" type — treat as OTHER
  PHOTO:          'OTHER',
  PDF:            'OTHER',
});

/**
 * Reconcile any document-type token to a canonical EVIDENCE_DOCUMENT_TYPES
 * value. Case-insensitive; tolerates spaces/hyphens.
 * @param {unknown} value
 * @returns {string|null} canonical value, or null if it cannot be reconciled.
 */
export const reconcileDocumentTypeHint = (value) => {
  if (typeof value !== 'string') return null;
  const key = value.trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (!key) return null;
  if (EVIDENCE_DOCUMENT_TYPES.includes(key)) return key;
  return DOCUMENT_TYPE_RECONCILIATION[key] || null;
};

// ── value-type + evidence-mode enums ────────────────────────────────────────
export const EVIDENCE_VALUE_TYPES = Object.freeze(['number', 'date', 'text', 'boolean', 'option']);
export const EVIDENCE_FIELD_MODES = Object.freeze(['keyword', 'nearby_value', 'option_alias', 'evidence_only']);
export const RED_FLAG_EVIDENCE_MODE = 'positive_only';

// ── Optional concept-binding metadata (Phase 2C-2c) ──────────────────────────
// Where a Pack evidence map's concept link came from. Never-PHI and OPTIONAL: a
// legacy / 2C-1 evidence map carrying none of these is fully valid and unchanged.
// The matcher (2C-3) later reads the PUBLISHED Pack evidence map (incl. this
// metadata), not the concept library directly.
export const EVIDENCE_CONCEPT_SOURCES = Object.freeze([
  'SPECIALTY_LIBRARY',
  'GLOBAL_LIBRARY',
  'DRAFTED',
  'MANUAL',
  'LOCAL_ONLY',
]);

// ── small pure helpers ───────────────────────────────────────────────────────

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);
const trimStr = (v) => (typeof v === 'string' ? v.trim() : '');

/**
 * Normalize a list of authored strings:
 *  - coerce to string, trim
 *  - drop empty strings
 *  - dedupe case-insensitively, preserving the first original casing
 * @param {unknown} list
 * @returns {string[]}
 */
export const normalizeStringList = (list) => {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const raw of list) {
    if (typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
};

/** Extract canonical option values from a field's options array. */
const optionValuesOf = (field) =>
  (Array.isArray(field?.options) ? field.options : [])
    .map((o) => (isPlainObject(o) ? o.value : o))
    .filter((v) => typeof v === 'string' && v.trim());

/** True if a value contains `undefined` anywhere in its structure. */
const containsUndefinedDeep = (value) => {
  if (value === undefined) return true;
  if (value === null || typeof value !== 'object') return false;
  return Object.values(value).some(containsUndefinedDeep);
};

/**
 * Normalize the OPTIONAL, Never-PHI concept-binding metadata shared by all four
 * evidence-map types (Phase 2C-2c). Lenient + defensive: every output key is
 * valid and defined, invalid values are omitted (never thrown, never undefined),
 * and absence is preserved. Returns a partial object holding ONLY the present,
 * valid keys — callers spread it and also use Object.keys(meta).length to decide
 * whether to keep an otherwise-empty map.
 *
 * - conceptId: trimmed string; omit if empty
 * - conceptSource: must be an EVIDENCE_CONCEPT_SOURCES value; else omit
 * - conceptConfidence: finite number clamped to [0,1]; else omit
 * - reviewedBySpecialist: kept only when it is a real boolean; else omit
 * - localAliases / localPhrases: normalized string lists; omit when empty
 * - localOverrides: kept only when a plain object with no undefined; else omit
 */
const normalizeConceptBinding = (raw) => {
  const out = {};
  if (!isPlainObject(raw)) return out;

  const conceptId = trimStr(raw.conceptId);
  if (conceptId) out.conceptId = conceptId;

  const conceptSource = trimStr(raw.conceptSource);
  if (EVIDENCE_CONCEPT_SOURCES.includes(conceptSource)) out.conceptSource = conceptSource;

  if (typeof raw.conceptConfidence === 'number' && Number.isFinite(raw.conceptConfidence)) {
    out.conceptConfidence = Math.min(1, Math.max(0, raw.conceptConfidence));
  }

  if (typeof raw.reviewedBySpecialist === 'boolean') {
    out.reviewedBySpecialist = raw.reviewedBySpecialist;
  }

  const localAliases = normalizeStringList(raw.localAliases);
  if (localAliases.length) out.localAliases = localAliases;

  const localPhrases = normalizeStringList(raw.localPhrases);
  if (localPhrases.length) out.localPhrases = localPhrases;

  if (isPlainObject(raw.localOverrides) && !containsUndefinedDeep(raw.localOverrides)) {
    out.localOverrides = { ...raw.localOverrides };
  }

  return out;
};

// ============================================================================
// NORMALIZERS
// Each returns a clean object, or `undefined` when the map has no meaningful
// content — callers OMIT the key entirely (never write `undefined` to
// Firestore, which the client SDK rejects). Legacy packs with no evidence maps
// therefore pass through unchanged.
// ============================================================================

/** Normalize a field-level evidence map. */
export const normalizeFieldEvidenceMap = (raw) => {
  if (!isPlainObject(raw)) return undefined;
  const canonicalLabel = trimStr(raw.canonicalLabel);
  const aliases = normalizeStringList(raw.aliases);
  const phrases = normalizeStringList(raw.phrases);
  const units = normalizeStringList(raw.units);
  const negativePhrases = normalizeStringList(raw.negativePhrases);
  const uncertaintyPhrases = normalizeStringList(raw.uncertaintyPhrases);
  const valueType = EVIDENCE_VALUE_TYPES.includes(raw.valueType) ? raw.valueType : '';
  const evidenceMode = EVIDENCE_FIELD_MODES.includes(raw.evidenceMode) ? raw.evidenceMode : '';
  const meta = normalizeConceptBinding(raw);

  // A concept link (or any binding metadata) keeps the map even with empty lists.
  const empty =
    !canonicalLabel && !valueType && !evidenceMode &&
    aliases.length === 0 && phrases.length === 0 && units.length === 0 &&
    negativePhrases.length === 0 && uncertaintyPhrases.length === 0 &&
    Object.keys(meta).length === 0;
  if (empty) return undefined;

  const out = { aliases, phrases, units, negativePhrases, uncertaintyPhrases, ...meta };
  if (canonicalLabel) out.canonicalLabel = canonicalLabel;
  if (valueType) out.valueType = valueType;
  if (evidenceMode) out.evidenceMode = evidenceMode;
  return out;
};

/** Normalize one option's evidence entry. */
const normalizeOneOptionEvidence = (raw) => {
  if (!isPlainObject(raw)) return undefined;
  const aliases = normalizeStringList(raw.aliases);
  const phrases = normalizeStringList(raw.phrases);
  const negativePhrases = normalizeStringList(raw.negativePhrases);
  const uncertaintyPhrases = normalizeStringList(raw.uncertaintyPhrases);
  const meta = normalizeConceptBinding(raw);
  if (!aliases.length && !phrases.length && !negativePhrases.length && !uncertaintyPhrases.length &&
      Object.keys(meta).length === 0) return undefined;
  return { aliases, phrases, negativePhrases, uncertaintyPhrases, ...meta };
};

/**
 * Normalize an option evidence map. Keeps only keys present in
 * validOptionValues (silently drops orphaned option keys). Returns undefined
 * when empty.
 */
export const normalizeOptionEvidenceMap = (raw, validOptionValues = []) => {
  if (!isPlainObject(raw)) return undefined;
  const valid = new Set((validOptionValues || []).filter((v) => typeof v === 'string'));
  const out = {};
  let any = false;
  for (const [optionValue, val] of Object.entries(raw)) {
    if (!valid.has(optionValue)) continue; // drop orphan
    const cleaned = normalizeOneOptionEvidence(val);
    if (cleaned) { out[optionValue] = cleaned; any = true; }
  }
  return any ? out : undefined;
};

/**
 * Normalize a red-flag evidence map. Always forces the two safety invariants:
 * evidenceMode = 'positive_only' and requireExplicitPositiveEvidence = true.
 */
export const normalizeRedFlagEvidenceMap = (raw) => {
  if (!isPlainObject(raw)) return undefined;
  const positivePhrases = normalizeStringList(raw.positivePhrases);
  const negativePhrases = normalizeStringList(raw.negativePhrases);
  const uncertaintyPhrases = normalizeStringList(raw.uncertaintyPhrases);
  const meta = normalizeConceptBinding(raw);
  if (!positivePhrases.length && !negativePhrases.length && !uncertaintyPhrases.length &&
      Object.keys(meta).length === 0) return undefined;
  return {
    positivePhrases,
    negativePhrases,
    uncertaintyPhrases,
    ...meta,                                 // optional binding metadata (Never-PHI)
    evidenceMode: RED_FLAG_EVIDENCE_MODE,    // always positive_only — forced AFTER meta
    requireExplicitPositiveEvidence: true,   // always true — metadata can never weaken safety
  };
};

/** Normalize an attachment-requirement evidence map. */
export const normalizeAttachmentEvidenceMap = (raw) => {
  if (!isPlainObject(raw)) return undefined;
  const aliases = normalizeStringList(raw.aliases);
  const phrases = normalizeStringList(raw.phrases);
  const seen = new Set();
  const documentTypeHints = [];
  if (Array.isArray(raw.documentTypeHints)) {
    for (const hint of raw.documentTypeHints) {
      const canon = reconcileDocumentTypeHint(hint);
      if (canon && !seen.has(canon)) { seen.add(canon); documentTypeHints.push(canon); }
    }
  }
  const meta = normalizeConceptBinding(raw);
  if (!aliases.length && !phrases.length && !documentTypeHints.length &&
      Object.keys(meta).length === 0) return undefined;
  return { documentTypeHints, aliases, phrases, ...meta };
};

/**
 * Return a new pack-like object with every evidence map normalized. Empty maps
 * are OMITTED (key deleted), never written as undefined. Pure + immutable.
 * Legacy packs with no evidence maps are returned structurally unchanged.
 * @param {Object} packLike
 * @returns {Object}
 */
export const normalizePackEvidenceMaps = (packLike) => {
  if (!isPlainObject(packLike)) return packLike;

  const fields = Array.isArray(packLike.fields)
    ? packLike.fields.map((field) => {
        if (!isPlainObject(field)) return field;
        const next = { ...field };
        const em = normalizeFieldEvidenceMap(field.evidenceMap);
        const oem = normalizeOptionEvidenceMap(field.optionEvidenceMap, optionValuesOf(field));
        if (em) next.evidenceMap = em; else delete next.evidenceMap;
        if (oem) next.optionEvidenceMap = oem; else delete next.optionEvidenceMap;
        return next;
      })
    : packLike.fields;

  const redFlags = Array.isArray(packLike.redFlags)
    ? packLike.redFlags.map((rf) => {
        if (!isPlainObject(rf)) return rf;
        const next = { ...rf };
        const em = normalizeRedFlagEvidenceMap(rf.evidenceMap);
        if (em) next.evidenceMap = em; else delete next.evidenceMap;
        return next;
      })
    : packLike.redFlags;

  const attachmentRequirements = Array.isArray(packLike.attachmentRequirements)
    ? packLike.attachmentRequirements.map((att) => {
        if (!isPlainObject(att)) return att;
        const next = { ...att };
        const em = normalizeAttachmentEvidenceMap(att.evidenceMap);
        if (em) next.evidenceMap = em; else delete next.evidenceMap;
        return next;
      })
    : packLike.attachmentRequirements;

  return { ...packLike, fields, redFlags, attachmentRequirements };
};

// ============================================================================
// VALIDATORS
// Run on the RAW authored pack (pre-normalization) so invalid enums, malformed
// shapes, and orphaned option keys are surfaced and can BLOCK publish. Absent
// or empty evidence maps are always valid (evidence maps are never required).
// Each returns string[] of human-readable error messages.
// ============================================================================

const listOfStringsValid = (v) =>
  v === undefined || (Array.isArray(v) && v.every((x) => typeof x === 'string'));

/**
 * Validate the OPTIONAL concept-binding metadata (Phase 2C-2c). Accepts but does
 * not require metadata; returns string[] of errors. Runs on the RAW authored map
 * so structurally-invalid metadata (bad source, out-of-range confidence, non-
 * boolean review flag, non-array local lists, non-object overrides) can BLOCK
 * publish. Absence of metadata is always valid (legacy / 2C-1 maps unchanged).
 */
const validateConceptBinding = (map, label) => {
  const errors = [];
  if (map.conceptId !== undefined && typeof map.conceptId !== 'string') {
    errors.push(`Evidence concept id for "${label}" must be text.`);
  }
  if (map.conceptSource !== undefined && !EVIDENCE_CONCEPT_SOURCES.includes(map.conceptSource)) {
    errors.push(`Evidence concept source for "${label}" must be one of: ${EVIDENCE_CONCEPT_SOURCES.join(', ')}.`);
  }
  if (map.conceptConfidence !== undefined && map.conceptConfidence !== null) {
    if (typeof map.conceptConfidence !== 'number' || !Number.isFinite(map.conceptConfidence) ||
        map.conceptConfidence < 0 || map.conceptConfidence > 1) {
      errors.push(`Evidence concept confidence for "${label}" must be a number between 0 and 1.`);
    }
  }
  if (map.reviewedBySpecialist !== undefined && typeof map.reviewedBySpecialist !== 'boolean') {
    errors.push(`Evidence "reviewedBySpecialist" for "${label}" must be true or false.`);
  }
  if (!listOfStringsValid(map.localAliases)) {
    errors.push(`Evidence "localAliases" for "${label}" must be a list of text values.`);
  }
  if (!listOfStringsValid(map.localPhrases)) {
    errors.push(`Evidence "localPhrases" for "${label}" must be a list of text values.`);
  }
  if (map.localOverrides !== undefined && !isPlainObject(map.localOverrides)) {
    errors.push(`Evidence "localOverrides" for "${label}" must be an object.`);
  }
  return errors;
};

/** Validate a field-level evidence map. */
export const validateFieldEvidenceMap = (evidenceMap, label = 'field') => {
  const errors = [];
  if (evidenceMap === undefined || evidenceMap === null) return errors;
  if (!isPlainObject(evidenceMap)) {
    errors.push(`Evidence map for "${label}" must be an object.`);
    return errors;
  }
  for (const key of ['aliases', 'phrases', 'units', 'negativePhrases', 'uncertaintyPhrases']) {
    if (!listOfStringsValid(evidenceMap[key])) {
      errors.push(`Evidence "${key}" for "${label}" must be a list of text values.`);
    }
  }
  if (evidenceMap.valueType !== undefined && evidenceMap.valueType !== '' &&
      !EVIDENCE_VALUE_TYPES.includes(evidenceMap.valueType)) {
    errors.push(`Evidence value type for "${label}" must be one of: ${EVIDENCE_VALUE_TYPES.join(', ')}.`);
  }
  if (evidenceMap.evidenceMode !== undefined && evidenceMap.evidenceMode !== '' &&
      !EVIDENCE_FIELD_MODES.includes(evidenceMap.evidenceMode)) {
    errors.push(`Evidence mode for "${label}" must be one of: ${EVIDENCE_FIELD_MODES.join(', ')}.`);
  }
  errors.push(...validateConceptBinding(evidenceMap, label));
  return errors;
};

/** Validate an option evidence map against the field's real option values. */
export const validateOptionEvidenceMap = (optionEvidenceMap, validOptionValues = [], label = 'field') => {
  const errors = [];
  if (optionEvidenceMap === undefined || optionEvidenceMap === null) return errors;
  if (!isPlainObject(optionEvidenceMap)) {
    errors.push(`Option evidence map for "${label}" must be an object.`);
    return errors;
  }
  const valid = new Set((validOptionValues || []).filter((v) => typeof v === 'string'));
  for (const [optionValue, val] of Object.entries(optionEvidenceMap)) {
    if (!valid.has(optionValue)) {
      errors.push(`Option evidence for "${label}" references unknown option "${optionValue}".`);
      continue;
    }
    if (!isPlainObject(val)) {
      errors.push(`Option evidence for "${label}" → "${optionValue}" must be an object.`);
      continue;
    }
    for (const key of ['aliases', 'phrases', 'negativePhrases', 'uncertaintyPhrases']) {
      if (!listOfStringsValid(val[key])) {
        errors.push(`Option evidence "${key}" for "${label}" → "${optionValue}" must be a list of text values.`);
      }
    }
    errors.push(...validateConceptBinding(val, `${label} → ${optionValue}`));
  }
  return errors;
};

/** Validate a red-flag evidence map (enforces the safety invariants). */
export const validateRedFlagEvidenceMap = (evidenceMap, label = 'red flag') => {
  const errors = [];
  if (evidenceMap === undefined || evidenceMap === null) return errors;
  if (!isPlainObject(evidenceMap)) {
    errors.push(`Evidence map for red flag "${label}" must be an object.`);
    return errors;
  }
  for (const key of ['positivePhrases', 'negativePhrases', 'uncertaintyPhrases']) {
    if (!listOfStringsValid(evidenceMap[key])) {
      errors.push(`Evidence "${key}" for red flag "${label}" must be a list of text values.`);
    }
  }
  if (evidenceMap.evidenceMode !== undefined && evidenceMap.evidenceMode !== RED_FLAG_EVIDENCE_MODE) {
    errors.push(`Red flag "${label}" evidence mode must remain "${RED_FLAG_EVIDENCE_MODE}".`);
  }
  if (evidenceMap.requireExplicitPositiveEvidence !== undefined &&
      evidenceMap.requireExplicitPositiveEvidence !== true) {
    errors.push(`Red flag "${label}" must keep requireExplicitPositiveEvidence = true (red flags are never auto-selected).`);
  }
  errors.push(...validateConceptBinding(evidenceMap, label));
  return errors;
};

/** Validate an attachment-requirement evidence map. */
export const validateAttachmentEvidenceMap = (evidenceMap, label = 'attachment') => {
  const errors = [];
  if (evidenceMap === undefined || evidenceMap === null) return errors;
  if (!isPlainObject(evidenceMap)) {
    errors.push(`Evidence map for attachment "${label}" must be an object.`);
    return errors;
  }
  for (const key of ['aliases', 'phrases']) {
    if (!listOfStringsValid(evidenceMap[key])) {
      errors.push(`Evidence "${key}" for attachment "${label}" must be a list of text values.`);
    }
  }
  if (evidenceMap.documentTypeHints !== undefined) {
    if (!Array.isArray(evidenceMap.documentTypeHints)) {
      errors.push(`Attachment "${label}" document type hints must be a list.`);
    } else {
      for (const hint of evidenceMap.documentTypeHints) {
        if (reconcileDocumentTypeHint(hint) === null) {
          errors.push(`Attachment "${label}" has an unrecognized document type hint "${hint}". Use the canonical evidence document types.`);
        }
      }
    }
  }
  errors.push(...validateConceptBinding(evidenceMap, label));
  return errors;
};

/**
 * Validate every evidence map in a pack-like object. Returns string[] of
 * errors (empty = valid). Pure.
 * @param {Object} packLike
 * @returns {string[]}
 */
export const validatePackEvidenceMaps = (packLike) => {
  const errors = [];
  if (!isPlainObject(packLike)) return errors;

  (packLike.fields || []).forEach((field) => {
    if (!isPlainObject(field)) return;
    const label = field.label || field.id || 'field';
    errors.push(...validateFieldEvidenceMap(field.evidenceMap, label));
    errors.push(...validateOptionEvidenceMap(field.optionEvidenceMap, optionValuesOf(field), label));
  });

  (packLike.redFlags || []).forEach((rf) => {
    if (!isPlainObject(rf)) return;
    errors.push(...validateRedFlagEvidenceMap(rf.evidenceMap, rf.label || rf.id || 'red flag'));
  });

  (packLike.attachmentRequirements || []).forEach((att) => {
    if (!isPlainObject(att)) return;
    errors.push(...validateAttachmentEvidenceMap(att.evidenceMap, att.label || att.id || 'attachment'));
  });

  return errors;
};
